import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { ROUTE_NAMES } from '@tsgecart/config';
import { cn } from '@tsgecart/ui';
import { User, Package, MapPin, Heart, Gift, Bell, LogOut, ShieldCheck, Menu, X } from 'lucide-react';
import { AUTH_ME_QUERY_KEY, useAppUser } from '../../../../lib/auth/use-app-user.js';

interface AccountNavItem {
  label: string;
  to: string;
  icon: typeof User;
}

const NAV_ITEMS: AccountNavItem[] = [
  { label: 'Profile', to: ROUTE_NAMES.accountProfile, icon: User },
  { label: 'Orders', to: ROUTE_NAMES.accountOrders, icon: Package },
  { label: 'Addresses', to: ROUTE_NAMES.accountAddresses, icon: MapPin },
  { label: 'Wishlist', to: ROUTE_NAMES.accountWishlist, icon: Heart },
  { label: 'Rewards', to: ROUTE_NAMES.accountRewards, icon: Gift },
  { label: 'Notifications', to: ROUTE_NAMES.accountNotifications, icon: Bell },
];

function navLinkClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'focus-visible:ring-brand flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium focus-visible:outline-none focus-visible:ring-2',
    isActive ? 'bg-brand/10 text-brand-foreground' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
  );
}

function mobileNavLinkClassName({ isActive }: { isActive: boolean }): string {
  return cn(
    'focus-visible:ring-brand flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium focus-visible:outline-none focus-visible:ring-2',
    isActive ? 'border-brand bg-brand/10 text-brand-foreground' : 'border-gray-200 text-gray-600 hover:bg-gray-50',
  );
}

export interface AccountLayoutProps {
  /** Optional explicit page content. When omitted, renders `<Outlet/>` so this layout can also be used as a parent route element for nested `/account/*` routes. */
  children?: ReactNode;
}

/**
 * Reusable shell for every `/account/*` page (profile, orders, addresses,
 * and the wishlist/rewards/notifications pages other agents build) --
 * profile summary header, side nav (desktop) / horizontal scroll tabs
 * (mobile), active-route highlighting, sign out, and an Admin Dashboard
 * link gated on `useAppUser().data.roles` (never Clerk claims), mirroring
 * `UserMenu`'s exact same rule. Assumes it renders inside an already-
 * authenticated context -- the actual `RequireAuth` wrap happens centrally
 * in App.tsx, this component does not duplicate that redirect logic.
 */
export function AccountLayout({ children }: AccountLayoutProps) {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: appUser } = useAppUser();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const canAccessAdmin = appUser?.roles.some((role) => role === 'ADMIN' || role === 'STAFF') ?? false;
  const displayName = user?.fullName ?? user?.firstName ?? user?.primaryEmailAddress?.emailAddress ?? 'Your account';
  const email = user?.primaryEmailAddress?.emailAddress ?? '';

  async function handleSignOut() {
    await signOut();
    queryClient.removeQueries({ queryKey: AUTH_ME_QUERY_KEY });
    navigate(ROUTE_NAMES.home, { replace: true });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-4 sm:p-6">
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt="" loading="lazy" className="h-14 w-14 shrink-0 rounded-full object-cover" />
        ) : (
          <span className="bg-brand text-brand-foreground flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-xl font-semibold">
            {displayName.charAt(0).toUpperCase()}
          </span>
        )}
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-gray-900">{displayName}</p>
          {email && <p className="truncate text-sm text-gray-500">{email}</p>}
        </div>
      </div>

      {/* Mobile nav: horizontal scroll of tabs, toggled by a "Menu" affordance for very small screens is unnecessary here since it already scrolls -- kept simple and always visible below sm. */}
      <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-1 lg:hidden" aria-label="Account navigation">
        <button
          type="button"
          onClick={() => setMobileNavOpen((open) => !open)}
          aria-expanded={mobileNavOpen}
          aria-label={mobileNavOpen ? 'Hide account navigation' : 'Show account navigation'}
          className="focus-visible:ring-brand flex shrink-0 items-center justify-center rounded-full border border-gray-200 p-2 text-gray-600 focus-visible:outline-none focus-visible:ring-2 sm:hidden"
        >
          {mobileNavOpen ? <X className="h-4 w-4" aria-hidden="true" /> : <Menu className="h-4 w-4" aria-hidden="true" />}
        </button>
        <nav
          aria-label="Account sections"
          className={cn('flex items-center gap-2 overflow-x-auto', !mobileNavOpen && 'hidden sm:flex')}
        >
          {NAV_ITEMS.map((item) => (
            <NavLink key={item.to} to={item.to} className={mobileNavLinkClassName} onClick={() => setMobileNavOpen(false)}>
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}
          {canAccessAdmin && (
            <NavLink to={ROUTE_NAMES.adminDashboard} className={mobileNavLinkClassName}>
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              Admin Dashboard
            </NavLink>
          )}
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </button>
        </nav>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[220px_1fr]">
        <nav aria-label="Account sections" className="hidden lg:block">
          <ul className="space-y-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} className={navLinkClassName}>
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </NavLink>
              </li>
            ))}
            {canAccessAdmin && (
              <li>
                <NavLink to={ROUTE_NAMES.adminDashboard} className={navLinkClassName}>
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                  Admin Dashboard
                </NavLink>
              </li>
            )}
            <li className="pt-2">
              <button
                type="button"
                onClick={() => void handleSignOut()}
                className="focus-visible:ring-brand flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Sign out
              </button>
            </li>
          </ul>
        </nav>

        <div className="min-w-0">{children ?? <Outlet />}</div>
      </div>
    </div>
  );
}
